import { Box, Divider, Menu, MenuItem } from "@mui/material";
import { MprcIconButton } from "components";
import { MprcButton } from "components/MprcButton";
import { MprcMenuItemCheck } from "components/MprcMenuItemCheck";
import * as React from "react";

////////////////////////////////////////////////////////////////////////////////

export type MprcDropdownItem<T> = {
    text: React.ReactNode;
    value: T;
    appendSeparator?: boolean;
    disabled?: boolean;
    checked?: boolean;
};

export type MprcDropdownItems<T> = MprcDropdownItem<T>[];

export type MprcDropDownItemAny = MprcDropdownItem<any>;

////////////////////////////////////////////////////////////////////////////////

export function MprcDropdownBuildGroupedItems<T>(data: MprcDropdownItem<T>[][]): MprcDropdownItem<T>[] {
    var result: MprcDropdownItem<T>[] = [];
    const gdata = data.filter((p) => p.length > 0);
    const gcount = gdata.length;

    gdata.forEach((g, gindex) => {
        if (g.length > 0) {
            g.forEach((v, i) =>
                result.push({
                    text: v.text,
                    value: v.value,
                    appendSeparator: gindex < gcount - 1 && i == g.length - 1,
                    disabled: v.disabled
                })
            );
        }
    });

    return result;
}

////////////////////////////////////////////////////////////////////////////////

export interface IMprcIconButtonDropdownProps<T> {
    icon: React.ReactNode;
    items: MprcDropdownItem<T>[];
    text?: string;
    title?: string;
    color?: MprcColors | "inherit";
    menuItemCheck?: boolean;
    selected?: T;
    separator?: "blank" | "dash";
    buttonSize?: "small" | "medium" | "large";
    disabled?: boolean;
    disablePaddingRight?: boolean;
    paddingVariant?: "default" | "dense" | "disabled";
    onChange: (selectedValue: T) => void;
}

type MprcIconButtonDropdownComponent<T = any> = React.FC<IMprcIconButtonDropdownProps<T>>;

export const MprcIconButtonDropdown: MprcIconButtonDropdownComponent = (props) => {
    const { items, separator = "dash", disablePaddingRight = false, paddingVariant = "default", title, icon, text, menuItemCheck, onChange, color, buttonSize, selected, disabled } = props;

    const [anchor, setAnchor] = React.useState<Element | null>(null);
    const padding = paddingVariant == "disabled" ? 0
        : paddingVariant == "dense"
            ? 0.5
            : undefined;

    const menuItems = React.useMemo(() => {
        var arr: React.ReactNode[] = [];
        items.forEach((v, index) => {
            if (menuItemCheck)
                arr.push(
                    <MprcMenuItemCheck
                        key={index}
                        item={v}
                        selectedItem={selected}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onChange(v.value ?? "?");
                            setAnchor(null);
                        }}
                    />
                );
            else
                arr.push(
                    <MenuItem
                        key={index}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onChange(v.value ?? "?");
                            setAnchor(null);
                        }}
                        disabled={v.disabled}
                    >
                        {v.text}
                    </MenuItem>
                );
            if (v.appendSeparator) {
                if (separator === "dash") arr.push(<Divider key={`${index}_divider`} />);
                if (separator === "blank") arr.push(<Box sx={{ mt: 1.5 }} key={`${index}_blank`} />);
            }
        });
        return arr;
    }, [items, selected]);

    return (
        <>
            {
                text ? (
                    <MprcButton
                        title={title}
                        color={color ?? "inherit"}
                        startIcon={icon}
                        variant="text"
                        text={text}
                        disabled={disabled}
                        onClick={(e) => {
                            e.preventDefault(); e.stopPropagation();
                            setAnchor(e.currentTarget);
                        }}
                    />
                ) : (
                    <MprcIconButton
                        title={title}
                        color={color ?? "inherit"}
                        size={buttonSize}
                        disabled={disabled}
                        onClick={(e) => {
                            e.preventDefault(); e.stopPropagation();
                            setAnchor(e.currentTarget);
                        }}
                        sx={{
                            paddingRight: disablePaddingRight ? 0 : padding,
                            paddingLeft: padding,
                            paddingTop: padding,
                            paddingBottom: padding,
                        }}
                    >
                        {icon}
                    </MprcIconButton>
                )
            }

            <Menu anchorEl={anchor} open={anchor != null} onClose={() => setAnchor(null)}>
                {menuItems}
            </Menu>
        </>
    );
};
